import axios, { CancelTokenSource } from 'axios';

import { CampaignType, CreateCampaign, DiallerType } from '~pages/CampaignManagement/domain';
import { APIError, UnsupportedStructureError } from '~services/Errors';

import { CampaignResponse, CampaignsResponseDecoder } from './domain';

export const getCampaignsByNextUrl = async (
  nextUrl: string,
  cancelToken?: CancelTokenSource,
): Promise<CampaignResponse | undefined> => {
  const path = nextUrl;
  let resp;

  try {
    resp = await axios.request({
      url: path,
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
      cancelToken: cancelToken ? cancelToken.token : undefined,
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return undefined;
    }

    if (axios.isAxiosError(e)) {
      // Response should always be defined if axios error
      throw new APIError(e.response!.status, (e.response!.data as { error: string }).error || e.message);
    }

    throw new APIError(-1, e as string);
  }

  const decoded = CampaignsResponseDecoder.run(resp.data);

  if (decoded.ok === false) {
    const err = new UnsupportedStructureError(decoded.error.message);

    console.error(decoded.error);
    console.error(err);
    throw err;
  }

  return decoded.result;
};

export const getCampaigns = async (
  search: string,
  archived: boolean | undefined,
  diallerType: DiallerType | undefined,
  campaignType: CampaignType | undefined,
  accessFilterId: number | undefined,
  cancelToken?: CancelTokenSource,
): Promise<CampaignResponse | undefined> => {
  const path = '/api/campaign/';
  const params = {
    search: search.toLowerCase() || undefined,
    archived: archived !== undefined ? archived.toString() : undefined,
    dialler_type: diallerType,
    campaign_type: campaignType,
    access_filter_id: accessFilterId,
  };
  let resp;

  try {
    resp = await axios.request({
      url: path,
      method: 'GET',
      params: params,
      headers: {
        Accept: 'application/json',
      },
      cancelToken: cancelToken ? cancelToken.token : undefined,
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return undefined;
    }

    if (axios.isAxiosError(e)) {
      // Response should always be defined if axios error
      throw new APIError(e.response!.status, (e.response!.data as { error: string }).error || e.message);
    }

    throw new APIError(-1, e as string);
  }

  const decoded = CampaignsResponseDecoder.run(resp.data);

  if (decoded.ok === false) {
    const err = new UnsupportedStructureError(decoded.error.message);

    console.error(decoded.error);
    console.error(err);
    throw err;
  }

  return decoded.result;
};

export const createCampaign = async (data: CreateCampaign): Promise<void> => {
  const path = '/api/campaign/';

  let body: any = {
    name: data.name,
    description: data.description,
    routing_profiles: data.routingProfiles,
    dialler_type: data.diallerType,
    campaign_type: data.campaignType,
    access_filter_ids: data.accessFilterIds,
  };

  if (data.predictiveSettings) {
    body = {
      ...body,
      predictive_settings: {
        source_phone_number: data.predictiveSettings.sourcePhoneNumber,
      },
    };
  }

  try {
    await axios.request({
      method: 'POST',
      url: path,
      data: body,
      headers: {
        Accept: 'application/json',
      },
    });
  } catch (e) {
    if (axios.isAxiosError(e)) {
      // Response should always be defined if axios error
      throw new APIError(e.response!.status, (e.response!.data as { error: string }).error || e.message);
    }

    throw new APIError(-1, e as string);
  }
};
